<template>
    <div>
        <!-- Services Grid -->
        <section class="py-20 bg-gradient-to-b from-gray-50 to-white">
            <div class="max-w-7xl mx-auto px-5 sm:px-10 md:px-12 lg:px-5">
                <div class="text-center mb-16" data-aos="fade-up" data-aos-duration="1000">
                    <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Comprehensive Environmental Solutions
                    </h2>
                    <p class="text-xl text-gray-600 max-w-3xl mx-auto">
                        Expert services delivered with precision, compliance, and environmental stewardship
                    </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <!-- Service Cards -->
                    <div v-for="(service, index) in services" :key="index" 
                         @mousemove="onMouseMove" @mouseleave="onMouseLeave" :style="cardStyle"
                         class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
                         data-aos="fade-up" 
                         :data-aos-duration="(index + 1) * 200">
                        
                        <!-- Gradient Overlay -->
                        <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        <!-- Card Content -->
                        <div class="relative p-8">
                            <!-- Icon -->
                            <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="service.icon"></path>
                                </svg>
                            </div>
                            
                            <!-- Content -->
                            <h3 class="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                {{ service.title }}
                            </h3>
                            <p class="text-gray-600 mb-6 leading-relaxed">
                                {{ service.description }}
                            </p>
                            
                            <!-- Features -->
                            <ul class="space-y-3 mb-6">
                                <li v-for="feature in service.features" :key="feature" class="flex items-start">
                                    <svg class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                    </svg>
                                    <span class="text-gray-700">{{ feature }}</span>
                                </li>
                            </ul>
                            
                            <!-- CTA -->
                            <router-link to="/contact" class="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-300">
                                Learn More
                                <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                                </svg>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Unified Purpose & CTA Section -->
        <section class="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
            <!-- Subtle Background Elements -->
            <div class="absolute top-0 left-0 w-64 h-64 bg-green-500/5 rounded-full blur-3xl"></div>
            <div class="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
            
            <div class="relative max-w-6xl mx-auto px-5 sm:px-10 md:px-12 lg:px-5">
                <!-- Main Header -->
                <div class="text-center mb-16" data-aos="fade-up" data-aos-duration="1000">
                    <h2 class="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                        Our Purpose
                    </h2>
                    <p class="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        The foundation of our commitment to environmental excellence and sustainable development
                    </p>
                </div>

                <!-- Content Grid -->
                <div class="grid lg:grid-cols-2 gap-16 items-center mb-16">
                    <!-- Vision & Mission -->
                    <div class="space-y-12" data-aos="fade-right" data-aos-duration="1000">
                        <!-- Vision -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                    </svg>
                                </div>
                                <h3 class="text-2xl font-semibold text-gray-900">Vision</h3>
                            </div>
                            <p class="text-gray-700 leading-relaxed pl-15">
                                To contribute towards the reduction of negative environmental impacts by enhancing the positive impacts through innovative solutions and sustainable practices.
                            </p>
                        </div>

                        <!-- Mission -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                                    <svg class="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                                    </svg>
                                </div>
                                <h3 class="text-2xl font-semibold text-gray-900">Mission</h3>
                            </div>
                            <p class="text-gray-700 leading-relaxed pl-15">
                                Through detailed impact assessments, comprehensive monitoring and thorough auditing, we keep abreast with industry's environmental impacts and assist organizations in managing these impacts and fulfilling their compliance obligations.
                            </p>
                        </div>
                    </div>

                    <!-- CTA Section -->
                    <div class="bg-white rounded-3xl shadow-xl p-8 lg:p-12 border border-gray-100" data-aos="fade-left" data-aos-duration="1000">
                        <div class="text-center space-y-6">
                            <!-- Icon -->
                            <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto">
                                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                </svg>
                            </div>
                            
                            <!-- Content -->
                            <h3 class="text-3xl font-light text-gray-900">
                                Ready to Start Your Environmental Project?
                            </h3>
                            <p class="text-lg text-gray-600 leading-relaxed">
                                Contact our expert team today for a comprehensive consultation and customized solution
                            </p>
                            
                            <!-- Buttons -->
                            <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                                <router-link to="/contact" class="px-8 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-all duration-300 shadow-lg">
                                    Schedule Consultation
                                </router-link>
                                <router-link to="/contact" class="px-8 py-3 bg-white text-gray-700 border border-gray-300 rounded-full font-medium hover:bg-gray-50 transition-all duration-300">
                                    Call 0814342904
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>

                </div>
        </section>
    </div>
</template>

<script>
import { ref, computed } from 'vue';
import AOS from "aos";
import "aos/dist/aos.css";

AOS.init();

function throttle(func, delay) {
    let lastCall = 0;
    return (...args) => {
        const now = new Date().getTime();
        if (now - lastCall < delay) {
            return;
        }
        lastCall = now;
        return func(...args);
    };
}

export default {
    name: 'CardTilt',
    setup() {
        const rotate = ref({ x: 0, y: 0 });

        const onMouseMove = throttle((e) => {
            const card = e.currentTarget;
            const box = card.getBoundingClientRect();
            const x = e.clientX - box.left;
            const y = e.clientY - box.top;
            const centerX = box.width / 2;
            const centerY = box.height / 2;
            const rotateX = (y - centerY) / 4;
            const rotateY = (centerX - x) / 4;

            rotate.value = { x: rotateX, y: rotateY };
        }, 100);

        const onMouseLeave = () => {
            rotate.value = { x: 0, y: 0 };
        };

        const cardStyle = computed(() => ({
            transform: `perspective(1000px) rotateX(${rotate.value.x}deg) rotateY(${rotate.value.y}deg) scale3d(1, 1, 1)`,
            transition: 'all 400ms cubic-bezier(0.03, 0.98, 0.52, 0.99) 0s',
        }));

        const services = ref([
            {
                title: "Environmental Impact Assessment (EIA)",
                description: "Comprehensive environmental impact assessments to ensure your projects meet all regulatory requirements and minimize ecological footprint.",
                icon: "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7",
                features: [
                    "Baseline environmental studies",
                    "Impact prediction and evaluation",
                    "Mitigation strategies",
                    "Regulatory compliance"
                ]
            },
            {
                title: "Water Use Licenses (WUL)",
                description: "Expert assistance with water use license applications and compliance monitoring for sustainable water resource management.",
                icon: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
                features: [
                    "Water use license applications",
                    "Water quality assessments",
                    "Sustainable water management",
                    "Compliance monitoring"
                ]
            },
            {
                title: "Atmospheric Emissions Licenses (AEL)",
                description: "Professional atmospheric emissions licensing services to ensure air quality compliance and environmental protection.",
                icon: "M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z",
                features: [
                    "Air quality assessments",
                    "Emissions inventory development",
                    "License application support",
                    "Compliance monitoring"
                ]
            },
            {
                title: "Internal & External Audits",
                description: "Thorough environmental audits to ensure compliance with regulations and identify improvement opportunities.",
                icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
                features: [
                    "Compliance audits",
                    "Environmental management systems",
                    "Performance evaluations",
                    "Corrective action plans"
                ]
            },
            {
                title: "Environmental Management Systems",
                description: "Development and implementation of comprehensive environmental management systems for sustainable operations.",
                icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
                features: [
                    "ISO 14001 implementation",
                    "System development",
                    "Training and awareness",
                    "Continuous improvement"
                ]
            },
            {
                title: "Waste Management",
                description: "Comprehensive waste management solutions including classification, minimization, and disposal strategies.",
                icon: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
                features: [
                    "Waste classification",
                    "Minimization strategies",
                    "Disposal planning",
                    "Regulatory compliance"
                ]
            },
            {
                title: "Compliance Monitoring",
                description: "Ongoing monitoring and reporting to ensure continued compliance with environmental regulations and permits.",
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                features: [
                    "Regular compliance checks",
                    "Performance monitoring",
                    "Regulatory reporting",
                    "Corrective actions"
                ]
            },
            {
                title: "Dust-Fallout Monitoring",
                description: "Specialized dust and particulate matter monitoring including PM2.5 and PM10 for air quality assessment.",
                icon: "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12",
                features: [
                    "PM2.5 and PM10 monitoring",
                    "Dust fallout assessment",
                    "Air quality analysis",
                    "Mitigation recommendations"
                ]
            },
            {
                title: "Water Quality Monitoring",
                description: "Comprehensive groundwater and surface water monitoring to assess water quality and environmental impact.",
                icon: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
                features: [
                    "Groundwater monitoring",
                    "Surface water assessment",
                    "Quality parameter analysis",
                    "Trend analysis"
                ]
            }
        ]);

        return {
            onMouseMove,
            onMouseLeave,
            cardStyle,
            services,
        };
    },
};
</script>

<style scoped>
.card {
    transition: all 400ms cubic-bezier(0.03, 0.98, 0.52, 0.99) 0s;
}
</style>